import { useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { BlockedState } from '../state/useUserState';

interface UseUserManagementBlockedLogsProps {
  // 차단목록 상태 (그룹화된 구조)
  blocked: BlockedState;
  setBlocked: (partial: Partial<BlockedState>) => void;

  // 알림
  showAlert: (title: string, message: string) => void;

  // 고객사 ID 결정 로직 (각 파일에서 다를 수 있음)
  getCustomerId: () => number | null;

  // 전체 이용자 관리 여부
  isAllUserManagement?: boolean;
}

/**
 * 사용자 관리 차단목록 로직 hook
 * admin/index.tsx와 superadmin/index.tsx에서 공통으로 사용되는 차단목록 로직을 관리합니다.
 */
export const useUserManagementBlockedLogs = ({
  blocked,
  setBlocked,
  showAlert,
  getCustomerId,
  isAllUserManagement = false,
}: UseUserManagementBlockedLogsProps) => {

  const loadBlockedLogs = useCallback(async () => {
    const customerId = getCustomerId();

    // admin/index.tsx의 경우: isAllUserManagement가 false이고 customerId가 없으면 리턴
    // superadmin/index.tsx의 경우: isAllUserManagement가 true이면 customerId가 null일 수 있음
    if (!customerId && !isAllUserManagement) {
      return;
    }

    try {
      setBlocked({ loading: true });
      // includeDeleted는 최신 값을 사용하기 위해 함수 호출 시점에 참조
      const data = await adminAPI.getBlockedSignups(customerId, 28, blocked.includeDeleted);
      setBlocked({ logs: data || [], loading: false });
    } catch (err) {
      console.error('차단목록 불러오기 실패:', err);
      setBlocked({ loading: false });
    }
    // blocked.includeDeleted를 의존성 배열에서 제거하여 무한 루프 방지
    // includeDeleted가 변경되면 상위 컴포넌트의 useEffect에서 loadBlockedLogs를 다시 호출함
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCustomerId, isAllUserManagement, setBlocked]);

  const handleClearBlockedLogsClick = useCallback(() => {
    // superadmin/index.tsx의 경우: isAllUserManagement일 때 경고 표시
    if (isAllUserManagement) {
      showAlert('알림', '전체 이용자 관리에서는 개별 고객사의 차단목록 삭제가 불가능합니다. 각 고객사 관리 화면에서 삭제해주세요.');
      return;
    }
    // admin/index.tsx의 경우: 확인 다이얼로그 열기
    setBlocked({ confirmOpen: true });
  }, [isAllUserManagement, showAlert, setBlocked]);

  const handleClearBlockedLogs = useCallback(async () => {
    const customerId = getCustomerId();
    if (!customerId) return;

    try {
      setBlocked({ loading: true, confirmOpen: false });
      await adminAPI.clearBlockedSignups(customerId);
      await loadBlockedLogs();
    } catch (err) {
      console.error('차단목록 삭제 실패:', err);
      showAlert('오류', '차단목록 삭제에 실패했습니다.');
      setBlocked({ loading: false });
    }
  }, [getCustomerId, loadBlockedLogs, showAlert, setBlocked]);

  return {
    loadBlockedLogs,
    handleClearBlockedLogsClick,
    handleClearBlockedLogs,
  };
};

